import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { deployRequest, deployUpload } from '@/api/guardianforge/deploy'
import type { MlflowCandidate, MlflowPreview, ReleaseManifest, TestVideoEntry } from '@/api/guardianforge/types'
import {
  DeploymentCard,
  DeploymentError,
  DeploymentJsonView,
  DeploymentSpinner,
} from '@/components/deployment/DeploymentUi'
import DecisionModal, { type DecisionModalInitial } from '@/components/deployment/DecisionModal'
import { useDeployApi } from '@/hooks/useDeployApi'
import { useAuth } from '@/auth/useAuth'

const CANDIDATE_LIMIT = 200
const TEST_VIDEO_MAX_BYTES = 50 * 1024 * 1024
const TEST_VIDEO_EXTENSIONS = ['.mp4', '.avi', '.mov', '.mkv', '.webm'] as const
const TEST_VIDEO_ACCEPT = TEST_VIDEO_EXTENSIONS.join(',')
const TEST_VIDEO_VALIDATION_MESSAGE =
  '50MB 이하의 MP4, AVI, MOV, MKV, WebM 동영상 파일만 업로드할 수 있습니다.'

function isValidTestVideo(file: File) {
  const fileName = file.name.toLowerCase()
  return (
    file.size <= TEST_VIDEO_MAX_BYTES &&
    TEST_VIDEO_EXTENSIONS.some((extension) => fileName.endsWith(extension))
  )
}

export default function CandidateReviewPage() {
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const canExecuteComparison = hasPermission('model-comparison:execute')
  const canApproveWithoutComparison =
    hasPermission('model-decision:write') && hasPermission('model-release:manage')
  const [filter, setFilter] = useState('')
  const [modelNameQuery, setModelNameQuery] = useState('')
  const [selected, setSelected] = useState<MlflowCandidate | null>(null)
  const candidates = useDeployApi<{ candidates: MlflowCandidate[] }>(
    '/mlflow/candidates',
    { query: { model_name: modelNameQuery || undefined, limit: CANDIDATE_LIMIT } },
    null,
  )

  const filteredCandidates = useMemo(() => {
    const list = candidates.data?.candidates ?? []
    if (!filter) return list
    const needle = filter.toLowerCase()
    return list.filter((candidate) =>
      `${candidate.name} ${candidate.run_id ?? ''} ${candidate.version}`.toLowerCase().includes(needle),
    )
  }, [candidates.data, filter])

  return (
    <>
      <h2>Candidate Review</h2>
      <p className="deployment-subtitle">학습 결과를 검증하고 comparison으로 사람 확인을 시작합니다.</p>

      <div className="deployment-toolbar">
        <input
          placeholder="model_name 검색"
          value={modelNameQuery}
          onChange={(event) => setModelNameQuery(event.target.value)}
          style={{ width: 340 }}
        />
        <input
          placeholder="결과 안에서 좁히기"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          style={{ width: 240 }}
        />
        <button onClick={() => candidates.refresh()}>새로고침</button>
        {candidates.loading && <DeploymentSpinner />}
      </div>

      <DeploymentError error={candidates.error} />

      <div className="deployment-grid cols-2">
        <DeploymentCard title="후보 목록">
          <div style={{ maxHeight: 480, overflow: 'auto' }}>
            <table className="deployment-table">
              <thead>
                <tr>
                  <th>name</th>
                  <th>version</th>
                  <th>run</th>
                </tr>
              </thead>
              <tbody>
                {filteredCandidates.map((candidate) => {
                  const isSelected = selected?.name === candidate.name && selected?.version === candidate.version
                  return (
                    <tr
                      key={`${candidate.name}:${candidate.version}`}
                      className={isSelected ? 'selected' : 'clickable'}
                      onClick={() => setSelected(candidate)}
                    >
                      <td className="deployment-mono">{candidate.name}</td>
                      <td className="deployment-mono">{candidate.version}</td>
                      <td className="deployment-mono deployment-muted">{candidate.run_id ?? '-'}</td>
                    </tr>
                  )
                })}
                {filteredCandidates.length === 0 && (
                  <tr>
                    <td colSpan={3} className="deployment-muted">
                      후보가 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {(candidates.data?.candidates.length ?? 0) >= CANDIDATE_LIMIT && (
            <div className="deployment-muted" style={{ marginTop: 6, fontSize: 12 }}>
              상위 {CANDIDATE_LIMIT}개만 표시됩니다. 보이지 않는 모델이 있다면 검색어를 입력하세요.
            </div>
          )}
        </DeploymentCard>

        {selected ? (
          <CandidateDetail
            key={`${selected.name}:${selected.version}`}
            candidate={selected}
            canExecuteComparison={canExecuteComparison}
            canApproveWithoutComparison={canApproveWithoutComparison}
            onComparisonCreated={(comparisonId) => navigate(`/deployment/comparisons/${comparisonId}`)}
            onApprovedWithoutComparison={() => navigate('/deployment/model-registry')}
          />
        ) : (
          <DeploymentCard title="상세 정보">
            <div className="deployment-muted">목록에서 후보를 선택하세요.</div>
          </DeploymentCard>
        )}
      </div>
    </>
  )
}

function CandidateDetail({
  candidate,
  canExecuteComparison,
  canApproveWithoutComparison,
  onComparisonCreated,
  onApprovedWithoutComparison,
}: {
  candidate: MlflowCandidate
  canExecuteComparison: boolean
  canApproveWithoutComparison: boolean
  onComparisonCreated: (comparisonId: string) => void
  onApprovedWithoutComparison: () => void
}) {
  const candidateFormat = useMemo(() => detectFormatFromModelName(candidate.name), [candidate.name])
  const previewPath = `/mlflow/models/${encodeURIComponent(candidate.name)}/versions/${encodeURIComponent(
    candidate.version,
  )}/deployment-preview`
  const preview = useDeployApi<MlflowPreview>(
    previewPath,
    { query: { include_artifact_summary: true, format: candidateFormat } },
    null,
  )
  const videos = useDeployApi<{ videos: TestVideoEntry[] }>('/test-videos', undefined, null)

  const suggestedModelKey =
    (preview.data?.register_payload?.model_key as string | undefined) ?? candidate.suggested_model_key ?? ''
  const baselineCandidates = useDeployApi<{ releases: ReleaseManifest[] }>(
    suggestedModelKey ? '/releases' : null,
    { query: { model_key: suggestedModelKey, limit: 20 } },
    null,
  )

  const [busy, setBusy] = useState(false)
  const [showApproval, setShowApproval] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [testVideoRef, setTestVideoRef] = useState('')
  const [testVideoUploading, setTestVideoUploading] = useState(false)
  const [testVideoError, setTestVideoError] = useState<string | null>(null)
  const testVideoFileRef = useRef<HTMLInputElement | null>(null)
  const [baselineMode, setBaselineMode] = useState<'none' | 'release' | 'ephemeral'>('none')
  const [baselineModelKey, setBaselineModelKey] = useState('')
  const [baselineVersion, setBaselineVersion] = useState('')
  const [ephemeralRef, setEphemeralRef] = useState<string | null>(null)
  const [ephemeralUploading, setEphemeralUploading] = useState(false)
  const [ephemeralError, setEphemeralError] = useState<string | null>(null)
  const ephemeralFileRef = useRef<HTMLInputElement | null>(null)
  const [config, setConfig] = useState({
    imgsz: 640,
    conf: 0.5,
    iou: 0.45,
    max_frames: 200,
    stride: 1,
    device: 'cpu',
  })

  useEffect(() => {
    const releases = baselineCandidates.data?.releases ?? []
    if (releases.length > 0 && baselineMode === 'none' && !baselineModelKey && !baselineVersion) {
      setBaselineMode('release')
      setBaselineModelKey(releases[0].model_key)
      setBaselineVersion(releases[0].version)
    }
  }, [baselineCandidates.data, baselineMode, baselineModelKey, baselineVersion])

  const candidateArtifactUri = useMemo(() => {
    return `/mlflow/models/${encodeURIComponent(candidate.name)}/versions/${encodeURIComponent(
      candidate.version,
    )}/artifact?format=${encodeURIComponent(candidateFormat)}`
  }, [candidate.name, candidate.version, candidateFormat])

  const baselineArtifactUri = useMemo(() => {
    if (baselineMode === 'release') {
      if (!baselineModelKey || !baselineVersion) return null
      return `/releases/${encodeURIComponent(baselineModelKey)}/${encodeURIComponent(baselineVersion)}/artifact`
    }
    if (baselineMode === 'ephemeral' && ephemeralRef) {
      return `/ephemeral-models/${encodeURIComponent(ephemeralRef)}/artifact`
    }
    return null
  }, [baselineMode, baselineModelKey, baselineVersion, ephemeralRef])

  const baselineIncomplete = baselineMode !== 'none' && !baselineArtifactUri

  const uploadTestVideo = async () => {
    if (!canExecuteComparison) return
    const file = testVideoFileRef.current?.files?.[0]
    if (!file) return
    if (!isValidTestVideo(file)) {
      setTestVideoError(TEST_VIDEO_VALIDATION_MESSAGE)
      return
    }
    setTestVideoUploading(true)
    setTestVideoError(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const result = await deployUpload<{ ref: string; bytes: number }>('/test-videos', formData)
      setTestVideoRef(result.ref)
      videos.refresh()
    } catch (err) {
      setTestVideoError(errorMessage(err))
    } finally {
      setTestVideoUploading(false)
    }
  }

  const uploadEphemeral = async () => {
    if (!canExecuteComparison) return
    const file = ephemeralFileRef.current?.files?.[0]
    if (!file) return
    setEphemeralUploading(true)
    setEphemeralError(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const result = await deployUpload<{ ref: string; bytes: number }>('/ephemeral-models', formData)
      setEphemeralRef(result.ref)
    } catch (err) {
      setEphemeralError(errorMessage(err))
    } finally {
      setEphemeralUploading(false)
    }
  }

  const submit = async () => {
    if (!canExecuteComparison) return
    setBusy(true)
    setError(null)
    try {
      const created = await deployRequest<{ id: string }>('/comparisons', {
        method: 'POST',
        body: {
          candidate_source: 'mlflow_model_version',
          candidate_ref: `${candidate.name}/${candidate.version}`,
          candidate_artifact_uri: candidateArtifactUri,
          test_video_ref: testVideoRef,
          config,
          baseline_model_key: baselineMode === 'release' ? baselineModelKey || undefined : undefined,
          baseline_version: baselineMode === 'release' ? baselineVersion || undefined : undefined,
          baseline_artifact_uri: baselineArtifactUri || undefined,
          ephemeral_refs: baselineMode === 'ephemeral' && ephemeralRef ? [ephemeralRef] : undefined,
          candidate_extra: {
            model_name: candidate.name,
            model_version: candidate.version,
            run_id: candidate.run_id,
            baseline_mode: baselineMode,
          },
        },
      })
      onComparisonCreated(created.id)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const artifactSummary = preview.data?.artifact
  const approvalInitial = useMemo(
    () =>
      decisionInitialFromRegisterPayload(
        preview.data?.register_payload,
        candidate.version,
      ),
    [candidate.version, preview.data?.register_payload],
  )
  const manualApprovalReady = Boolean(
    canApproveWithoutComparison &&
      preview.data &&
      !preview.loading &&
      !preview.error &&
      approvalInitial.modelKey.trim() &&
      approvalInitial.version.trim() &&
      approvalInitial.format.trim(),
  )

  return (
    <DeploymentCard title={`${candidate.name} / ${candidate.version}`}>
      <DeploymentError error={preview.error} />
      {preview.loading && <DeploymentSpinner />}
      {preview.data && (
        <>
          <div className="deployment-grid cols-2">
            <div>
              <label>Suggested model_key</label>
              <div className="deployment-mono">{(preview.data.register_payload.model_key as string) ?? '-'}</div>
            </div>
            <div>
              <label>Release version (MLflow)</label>
              <div className="deployment-mono">{candidate.version}</div>
            </div>
            <div>
              <label>Run ID</label>
              <div className="deployment-mono">{(preview.data.register_payload.mlflow_run_id as string) ?? '-'}</div>
            </div>
            <div>
              <label>Format</label>
              <div className="deployment-mono">{(preview.data.register_payload.format as string) ?? '-'}</div>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <label>Artifact 요약</label>
            <div className="deployment-flex">
              <span className={`deployment-badge ${artifactSummary?.exists ? 'ok' : 'bad'}`}>
                {artifactSummary?.exists ? 'exists' : 'missing'}
              </span>
              {artifactSummary?.file_count !== undefined && (
                <span className="deployment-muted">{artifactSummary.file_count}개 file</span>
              )}
              {artifactSummary?.truncated && <span className="deployment-badge warn">truncated</span>}
            </div>
            {artifactSummary?.sha256 && (
              <div className="deployment-mono deployment-muted" style={{ marginTop: 4 }}>
                sha256: {artifactSummary.sha256}
              </div>
            )}
          </div>

          <h3 style={{ marginTop: 18 }}>비교 실행</h3>
          <DeploymentError error={videos.error} />
          <div className="deployment-grid cols-2">
            <div>
              <label>Test Video</label>
              <select value={testVideoRef} onChange={(event) => setTestVideoRef(event.target.value)}>
                <option value="">(선택)</option>
                {videos.data?.videos.map((video) => (
                  <option key={video.ref} value={video.ref}>
                    {video.ref} ({Math.round(video.bytes / 1024)} KB)
                  </option>
                ))}
              </select>
              <div className="deployment-upload-row" style={{ marginTop: 8 }}>
                <input
                  type="file"
                  ref={testVideoFileRef}
                  accept={TEST_VIDEO_ACCEPT}
                  disabled={!canExecuteComparison || testVideoUploading}
                />
                <button onClick={uploadTestVideo} disabled={!canExecuteComparison || testVideoUploading}>
                  {testVideoUploading ? '업로드 중...' : '영상 업로드'}
                </button>
              </div>
              {testVideoError && <div className="deployment-error-box">{testVideoError}</div>}
            </div>
            <div>
              <label>Baseline</label>
              <select value={baselineMode} onChange={(event) => setBaselineMode(event.target.value as typeof baselineMode)}>
                <option value="none">없음 (candidate 단독 측정)</option>
                <option value="release">기존 registered release</option>
                <option value="ephemeral">ad-hoc 모델 업로드 (1회용)</option>
              </select>
              {baselineMode === 'release' && (
                <div style={{ marginTop: 6 }}>
                  {(baselineCandidates.data?.releases ?? []).length > 0 ? (
                    <select
                      value={`${baselineModelKey}|${baselineVersion}`}
                      onChange={(event) => {
                        const [modelKey, version] = event.target.value.split('|')
                        setBaselineModelKey(modelKey)
                        setBaselineVersion(version)
                      }}
                    >
                      {(baselineCandidates.data?.releases ?? []).map((release) => (
                        <option key={`${release.model_key}|${release.version}`} value={`${release.model_key}|${release.version}`}>
                          {release.model_key}@{release.version}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="deployment-muted">동일 model_key의 registered release가 없습니다.</div>
                  )}
                  <div className="deployment-flex" style={{ marginTop: 6 }}>
                    <input placeholder="model_key 수동" value={baselineModelKey} onChange={(event) => setBaselineModelKey(event.target.value)} />
                    <input placeholder="version 수동" value={baselineVersion} onChange={(event) => setBaselineVersion(event.target.value)} />
                  </div>
                </div>
              )}
              {baselineMode === 'ephemeral' && (
                <div style={{ marginTop: 6 }}>
                  <div className="deployment-upload-row">
                    <input type="file" ref={ephemeralFileRef} accept=".pt,.onnx" disabled={!canExecuteComparison || ephemeralUploading} />
                    <button onClick={uploadEphemeral} disabled={!canExecuteComparison || ephemeralUploading || !!ephemeralRef}>
                      {ephemeralUploading ? '업로드 중...' : '업로드'}
                    </button>
                    {ephemeralRef && (
                      <button
                        className="ghost"
                        onClick={async () => {
                          await deployRequest(`/ephemeral-models/${encodeURIComponent(ephemeralRef)}`, { method: 'DELETE' }).catch(() => null)
                          setEphemeralRef(null)
                        }}
                      >
                        취소
                      </button>
                    )}
                  </div>
                  {ephemeralRef && (
                    <div className="deployment-muted deployment-mono" style={{ marginTop: 4 }}>
                      {ephemeralRef} (1회 비교 후 자동 삭제)
                    </div>
                  )}
                  {ephemeralError && <div className="deployment-error-box">{ephemeralError}</div>}
                </div>
              )}
            </div>
          </div>

          <div className="deployment-grid cols-4" style={{ marginTop: 12 }}>
            <NumberField label="imgsz" value={config.imgsz} onChange={(imgsz) => setConfig({ ...config, imgsz })} />
            <NumberField label="conf" value={config.conf} step={0.05} onChange={(conf) => setConfig({ ...config, conf })} />
            <NumberField label="iou" value={config.iou} step={0.05} onChange={(iou) => setConfig({ ...config, iou })} />
            <NumberField label="max_frames" value={config.max_frames} onChange={(max_frames) => setConfig({ ...config, max_frames })} />
          </div>

          {error && <div className="deployment-error-box">{error}</div>}

          <div className="deployment-flex" style={{ marginTop: 12 }}>
            <button
              className="primary"
              onClick={submit}
              disabled={!canExecuteComparison || busy || !testVideoRef || !candidateArtifactUri || baselineIncomplete}
            >
              {busy ? '비교 생성 중...' : '비교 시작'}
            </button>
            <button
              className="primary"
              onClick={() => setShowApproval(true)}
              disabled={!manualApprovalReady}
            >
              비교 없이 승인·등록
            </button>
            <span className="deployment-muted">
              {baselineIncomplete
                ? baselineMode === 'ephemeral'
                  ? 'ad-hoc 모델 업로드를 완료하거나 baseline을 없음으로 바꾸세요.'
                  : 'baseline release를 선택하거나 baseline을 없음으로 바꾸세요.'
                : '결과는 Comparisons 화면에서 확인합니다.'}
            </span>
          </div>

          <details style={{ marginTop: 16 }}>
            <summary className="deployment-muted">Preview JSON</summary>
            <DeploymentJsonView data={preview.data} />
          </details>

          {showApproval && manualApprovalReady && (
            <DecisionModal
              comparisonId={null}
              candidateSource="mlflow_model_version"
              candidateRef={`${candidate.name}/${candidate.version}`}
              initial={approvalInitial}
              kind="approve"
              onClose={() => setShowApproval(false)}
              onDone={onApprovedWithoutComparison}
            />
          )}
        </>
      )}
    </DeploymentCard>
  )
}

function NumberField({
  label,
  value,
  step,
  onChange,
}: {
  label: string
  value: number
  step?: number
  onChange: (value: number) => void
}) {
  return (
    <div>
      <label>{label}</label>
      <input type="number" step={step} value={value} onChange={(event) => onChange(Number(event.target.value))} />
    </div>
  )
}

function detectFormatFromModelName(modelName: string): string {
  if (modelName.endsWith('_openvino_int8')) return 'openvino_int8'
  if (modelName.endsWith('_openvino_fp16')) return 'openvino_fp16'
  if (modelName.endsWith('_openvino_fp32')) return 'openvino_fp32'
  return 'pt'
}

function decisionInitialFromRegisterPayload(
  payload: Record<string, unknown> | undefined,
  mlflowModelVersion: string,
): DecisionModalInitial {
  return {
    modelKey: requiredString(payload?.model_key),
    version: mlflowModelVersion,
    format: requiredString(payload?.format),
    mlflowRunId: optionalPayloadString(payload?.mlflow_run_id),
    mlflowModelName: optionalPayloadString(payload?.mlflow_model_name),
    mlflowModelVersion,
    artifactPath: optionalPayloadString(payload?.artifact_path),
    datasetId: optionalPayloadString(payload?.dataset_id),
    watchId: optionalPayloadString(payload?.watch_id),
    cvatProjectId: optionalPayloadNumber(payload?.cvat_project_id),
  }
}

function requiredString(value: unknown): string {
  return value === null || value === undefined ? '' : String(value)
}

function optionalPayloadString(value: unknown): string | undefined {
  const text = requiredString(value)
  return text || undefined
}

function optionalPayloadNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined
  const number = Number(value)
  return Number.isFinite(number) ? number : undefined
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message
  if (typeof err === 'object' && err !== null && 'message' in err) {
    return String((err as { message: unknown }).message)
  }
  return String(err)
}
